import instanceAxios from '@/util/axios';
import {
  addFavoriteUrl,
  removeFavoriteUrl,
  checkUserFavoriteUrl,
  checkMultipleFavoritesUrl,
  addOrUpdateRatingUrl,
  getUserRatingUrl,
  getAdAverageRatingUrl,
  getMultipleAdsAverageRatingsUrl,
  addAdCommentUrl,
  getAdCommentsUrl,
  updateAdCommentUrl,
  deleteAdCommentUrl,
  getAdCommentsCountUrl,
  getMultipleAdsCommentsCountsUrl,
  dependentOptions
} from '@/constants';
import { LOCATIONS, TARGET_AUDIENCES, CATEGORIES_WITH_SUBCATEGORIES } from '@/constants/businessAds.constants';
import { User } from '@sm-dashboard/types';

// Updated BusinessAd interface to match the new API response
export interface BusinessAd {
  _id: string;
  id: string;
  submitterType: 'shaliach' | 'publisher';
  businessName: string;
  businessNameForInvoicing?: string;
  businessEmail?: string;
  businessPhone: string;
  hasWhatsapp: boolean;
  category: string;
  subCategory: string[];
  shortDescription: string;
  fullDescription?: string;
  location: string[];
  minPrice?: number;
  maxPrice?: number;
  targetAudience: string[];
  shabadHouseDiscount?: string;
  mainImage?: string;
  additionalMedia: string[];
  websiteLink?: string;
  facebookLink?: string;
  instagramLink?: string;
  recommendations?: string;
  paymentPlan: 'monthly' | 'yearly' | 'none';
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  updatedAt: string;
}

export interface BusinessAdsResponse {
  rows: BusinessAd[];
  count: number;
}

export interface FilterState {
  category: string;
  subCategory: string;
  targetAudience: string[];
  location: string[];
  priceRange: [number, number];
}

export interface AppliedFilters {
  category: string;
  subCategory: string;
  targetAudience: string[];
  location: string[];
  priceRange: [number, number];
}

export interface FavoritesMap {
  [adId: string]: boolean;
}

export interface RatingData {
  averageRating: number;
  totalRatings: number;
}

export interface RatingsMap {
  [adId: string]: RatingData;
}

export interface Comment {
  _id: string;
  shaliachId: User['id'];
  shaliachName: string;
  adId: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

export interface CommentsResponse {
  comments: Comment[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

export interface CommentCount {
  [adId: string]: number;
}

// --- REWRITTEN HELPER FUNCTIONS ---

// This function is now much simpler. It directly accesses properties.
// It keeps a mapping for old Hebrew field names to maintain compatibility with calling code.
export const getFieldValue = (ad: BusinessAd, fieldName: string): string => {
  const fieldMap: { [key: string]: keyof BusinessAd } = {
    'שם העסק לתצוגה': 'businessName',
    'category': 'category',
    'subcategory': 'subCategory',
    'תיאור קצר': 'shortDescription',
    'תיאור מפורט': 'fullDescription',
    'טלפון העסק לתצוגה': 'businessPhone',
    'טלפון': 'businessPhone',
    'קהל יעד': 'targetAudience',
    'מיקום': 'location',
  };
  
  const key = fieldMap[fieldName] || (fieldName as keyof BusinessAd);
  
  if (key in ad) {
    const value = ad[key as keyof BusinessAd];
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return String(value || '');
  }
  return '';
};

export const getImageUrl = (ad: BusinessAd): string => {
  return ad.mainImage || '';
};

export const hasWhatsApp = (ad: BusinessAd): boolean => {
  return ad.hasWhatsapp;
};

export const handleWhatsAppClick = (ad: BusinessAd, isMobile: boolean = false) => {
  const phone = ad.businessPhone;
  const businessName = ad.businessName;

  if (!phone) return;

  // If desktop, show popup with phone number
  if (!isMobile) {
    const action = hasWhatsApp(ad) ? 'WhatsApp' : 'טלפון';
    const message = `${businessName}\n\n${action}: ${phone}\n\n${
      hasWhatsApp(ad) ? 'לשליחת הודעה בווטסאפ' : 'למתקשרים'
    }`;

    if (confirm(message)) {
      if (hasWhatsApp(ad)) {
        const whatsappMessage = `שלום, ראיתי את המודעה שלכם עבור ${businessName} במערכת חב"ד ישראל`;
        const whatsappUrl = `https://wa.me/972${phone.replace(/^0/, '')}?text=${encodeURIComponent(whatsappMessage)}`;
        window.open(whatsappUrl, '_blank');
      } else {
        navigator.clipboard.writeText(phone)
          .then(() => alert('המספר הועתק ללוח'))
          .catch(() => alert(`מספר הטלפון: ${phone}`));
      }
    }
    return;
  }

  // Mobile behavior - direct action
  if (hasWhatsApp(ad)) {
    const message = `שלום, ראיתי את המודעה שלכם עבור ${businessName} במערכת חב"ד ישראל`;
    const whatsappUrl = `https://wa.me/972${phone.replace(/^0/, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  } else {
    const phoneUrl = `tel:${phone}`;
    window.open(phoneUrl, '_self');
  }
};


// --- API FUNCTIONS (UNCHANGED) ---
// ... (addFavorite, removeFavorite, etc. are fine as they only use adId)
export const addFavorite = async (adId: string): Promise<boolean> => {
  try {
    await instanceAxios.post(addFavoriteUrl, { adId });
    return true;
  } catch (error) {
    console.error('Error adding favorite:', error);
    return false;
  }
};

export const removeFavorite = async (adId: string): Promise<boolean> => {
  try {
    await instanceAxios.delete(`${removeFavoriteUrl}/${adId}`);
    return true;
  } catch (error) {
    console.error('Error removing favorite:', error);
    return false;
  }
};

export const checkUserFavorite = async (adId: string): Promise<boolean> => {
  try {
    const response = await instanceAxios.get(`${checkUserFavoriteUrl}/${adId}`);
    return response.data.isFavorited;
  } catch (error) {
    console.error('Error checking user favorite:', error);
    return false;
  }
};

export const checkMultipleFavorites = async (adIds: string[]): Promise<FavoritesMap> => {
  try {
    const response = await instanceAxios.post(checkMultipleFavoritesUrl, { adIds });
    return response.data.favorites;
  } catch (error) {
    console.error('Error checking multiple favorites:', error);
    return {};
  }
};

// Rating API functions
export const addOrUpdateRating = async (adId: string, rating: number): Promise<boolean> => {
  try {
    await instanceAxios.post(addOrUpdateRatingUrl, { adId, rating });
    return true;
  } catch (error) {
    console.error('Error adding/updating rating:', error);
    return false;
  }
};

export const getUserRating = async (adId: string): Promise<number | null> => {
  try {
    const response = await instanceAxios.get(`${getUserRatingUrl}/${adId}`);
    return response.data.userRating;
  } catch (error) {
    console.error('Error getting user rating:', error);
    return null;
  }
};

export const getAdAverageRating = async (adId: string): Promise<RatingData> => {
  try {
    const response = await instanceAxios.get(`${getAdAverageRatingUrl}/${adId}`);
    return {
      averageRating: response.data.averageRating,
      totalRatings: response.data.totalRatings
    };
  } catch (error) {
    console.error('Error getting average rating:', error);
    return { averageRating: 0, totalRatings: 0 };
  }
};

export const getMultipleAdsAverageRatings = async (adIds: string[]): Promise<RatingsMap> => {
  try {
    const response = await instanceAxios.post(getMultipleAdsAverageRatingsUrl, { adIds });
    return response.data.ratings;
  } catch (error) {
    console.error('Error getting multiple ads average ratings:', error);
    return {};
  }
};

// Comment API functions
export const addComment = async (adId: string, content: string): Promise<Comment | null> => {
  try {
    const response = await instanceAxios.post(addAdCommentUrl, { adId, content });
    return response.data.comment;
  } catch (error) {
    console.error('Error adding comment:', error);
    return null;
  }
};

export const getAdComments = async (adId: string, page = 0, limit = 10): Promise<CommentsResponse> => {
  try {
    const response = await instanceAxios.get(`${getAdCommentsUrl}/${adId}?page=${page}&limit=${limit}`);
    return response.data;
  } catch (error) {
    console.error('Error getting comments:', error);
    return {
      comments: [],
      pagination: {
        page: 0,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasMore: false
      }
    };
  }
};

export const updateComment = async (commentId: string, content: string): Promise<Comment | null> => {
  try {
    const response = await instanceAxios.put(`${updateAdCommentUrl}/${commentId}`, { content });
 
    return response.data.comment;
  } catch (error) {
    console.error('Error updating comment:', error);
    if (error && typeof error === 'object' && 'response' in error) {
      console.error('Error details:', (error as { response?: { data?: unknown } }).response?.data);
    }
    return null;
  }
};

export const deleteComment = async (commentId: string): Promise<boolean> => {
  try {
    await instanceAxios.delete(`${deleteAdCommentUrl}/${commentId}`);
    return true;
  } catch (error) {
    console.error('Error deleting comment:', error);
    return false;
  }
};

export const getAdCommentsCount = async (adId: string): Promise<number> => {
  try {
    const response = await instanceAxios.get(`${getAdCommentsCountUrl}/${adId}`);
    return response.data.count;
  } catch (error) {
    console.error('Error getting comments count:', error);
    return 0;
  }
};

export const getMultipleAdsCommentsCounts = async (adIds: string[]): Promise<CommentCount> => {
  try {
    const response = await instanceAxios.post(getMultipleAdsCommentsCountsUrl, { adIds });
    return response.data.counts;
  } catch (error) {
    console.error('Error getting multiple ads comments counts:', error);
    return {};
  }
};


// --- REWRITTEN FILTERING LOGIC ---

// Updated to work with the new data model and pull options from constants
export const getFilterOptions = (ads: BusinessAd[]) => { // ads parameter is no longer used but kept for compatibility
  const categories = CATEGORIES_WITH_SUBCATEGORIES.map(c => c.name);
  const subCategories = CATEGORIES_WITH_SUBCATEGORIES.flatMap(c => c.subcategories);

  return {
    categories: categories.sort(),
    subCategories: Array.from(new Set(subCategories)).sort(), // Remove duplicates and sort
    targetAudiences: TARGET_AUDIENCES.sort(),
    locations: LOCATIONS.sort(),
    // Price range is not available in the new model in the same way, defaulting to a wide range
    priceRange: [0, 10000] as [number, number]
  };
};

// Updated to work with the new data model
export const filterAdsBySearch = (ads: BusinessAd[], searchTerm: string): BusinessAd[] => {
  if (!searchTerm) return ads;
  const lowercasedTerm = searchTerm.toLowerCase();

  return ads.filter(ad => 
    ad.businessName?.toLowerCase().includes(lowercasedTerm) ||
    ad.category?.toLowerCase().includes(lowercasedTerm) ||
    ad.shortDescription?.toLowerCase().includes(lowercasedTerm) ||
    ad.fullDescription?.toLowerCase().includes(lowercasedTerm)
  );
};

// Updated to work with the new data model
export const filterAdsByFilters = (ads: BusinessAd[], appliedFilters: AppliedFilters): BusinessAd[] => {
  return ads.filter(ad => {
    if (appliedFilters.category && ad.category !== appliedFilters.category) {
      return false;
    }
    if (appliedFilters.subCategory && !ad.subCategory?.includes(appliedFilters.subCategory)) {
      return false;
    }
    if (appliedFilters.targetAudience.length > 0) {
      const hasMatchingAudience = appliedFilters.targetAudience.some(filterAudience =>
        ad.targetAudience?.includes(filterAudience)
      );
      if (!hasMatchingAudience) return false;
    }
    if (appliedFilters.location.length > 0) {
      const hasMatchingLocation = appliedFilters.location.some(filterLocation =>
        ad.location?.includes(filterLocation)
      );
      if (!hasMatchingLocation) return false;
    }
    // Price range filtering is omitted as it's not a numeric field in the new model.
    return true;
  });
};


export const filterAds = (ads: BusinessAd[], searchTerm: string, appliedFilters: AppliedFilters): BusinessAd[] => {
  if (searchTerm) {
    return filterAdsBySearch(ads, searchTerm);
  }
  return filterAdsByFilters(ads, appliedFilters);
};

export const BUSINESS_ADS_FORM_IDS = ['68301b71353ca3296b3f2924', '68a4435e72e0f79650984b9f'];

// Updated to work with the new data model
export const getAdditionalImages = (ad: BusinessAd): string[] => {
  const mainImage = ad.mainImage || '';
  if (!ad.additionalMedia || !Array.isArray(ad.additionalMedia)) return [];
  
  return ad.additionalMedia.filter(mediaUrl => {
      const isNotMainImage = mediaUrl !== mainImage && mediaUrl.trim() !== '';
      const isImageFile = /\.(jpg|jpeg|png|gif|svg)(\?.*)?$/i.test(mediaUrl);
      return isNotMainImage && isImageFile;
  });
};

// Updated to work with the new data model
export const getAdditionalVideos = (ad: BusinessAd): string[] => {
    if (!ad.additionalMedia || !Array.isArray(ad.additionalMedia)) return [];

    return ad.additionalMedia.filter(mediaUrl => {
        const isVideoFile = /\.(mp4|webm|ogg)(\?.*)?$/i.test(mediaUrl) || mediaUrl.includes('youtube.com') || mediaUrl.includes('vimeo.com');
        return isVideoFile;
    });
};

// These can likely be removed if `businessAds.constants.ts` is the source of truth now.
export const getSubcategoriesByCategory = (category: string): string[] => {
  const categoryOption = dependentOptions.find(option => option.value === category);
  return categoryOption?.children ? categoryOption.children.map(child => child.value as string) : [];
};

export const getBusinessCategories = (): string[] => {
  return dependentOptions.map(option => option.value as string);
};

export const getAllSubcategories = (): string[] => {
  return dependentOptions.flatMap(option =>
    option.children ? option.children.map(child => child.value as string) : []
  );
};
